import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBullhorn } from "@fortawesome/free-solid-svg-icons";
import { defaultTableOption, rowPerPage } from "constants/table";
import CustomDataTable from "components/customs/table";
import { Link } from "react-router-dom";
import { fetchTable } from "services/services.projects";
import { Toast, hasPermission } from "utility/helpers";
import { genericDelete, genericGet } from "services/services.general";
import Swal from "sweetalert2";
import CommentsModal from "components/customs/comments-modal";
import { useNavigate } from "react-router";
import { HeaderText, AddButton } from "assets/js/Styles";
import styled from "styled-components";
import { ADD_ACTION } from "constants/actions";
import newsColumns from "./columns";

const ExpandedTableContainer = styled.div`
  padding-top: 1rem;
  padding-bottom: 1rem;
  padding-left: 8rem;
  padding-right: 8rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);

  @media (max-width: 575.98px) {
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const HeaderTextSection = styled.div`
  display: grid;
  justify-content: space-between;
  grid-auto-flow: column;
  align-items: center;

  h2 {
    margin-bottom: 0;
  }

  a {
    text-decoration: none;
  }

  @media (max-width: 575.98px) {
    grid-template-columns: 1fr 50px;

    a {
      display: flex;
      justify-content: flex-end;
    }

    padding-top: 4rem;
  }
`;

const StyledAddButton = styled(AddButton)`
  width: 130px;
  background: ${({ theme }) => theme.colors.buttonBlue};

  @media (max-width: 1265px) {
    width: 100%;
  }
`;

const AddButtonText = styled.span`
  padding-left: 8px;
  margin: 0;
  font-weight: 500;
  letter-spacing: 0.63px;
`;

const TableSection = styled.div`
  margin-top: 44px;
`;

const Wrapper = styled.div`
  margin-top: 50px;
`;

const CardSection = styled.div`
  background: #ffffff;
  border-radius: 15px;
  box-shadow: 4px 9px 17px -1px #00000014;
`;

const CardContainer = styled.div`
  padding-top: 24px;
  padding-left: 50px;
  padding-right: 50px;
  padding-bottom: 24px;
`;

const ExpandedComponentLabel = styled.h5`
  font-weight: bold;
`;

const ExpandedComponentFileSection = styled.p`
  display: flex;
  flex-direction: column;
  margin-bottom: 0;
`;

const ExpandedComponent = ({ data }) => {
  return (
    <ExpandedTableContainer>
      {data?.description ? (
        <>
          <ExpandedComponentLabel>{"Description"}</ExpandedComponentLabel>

          <p>{data?.description}</p>
        </>
      ) : null}

      {data?.message ? (
        <>
          <ExpandedComponentLabel>{"Message"}</ExpandedComponentLabel>

          <p>{data?.message}</p>
        </>
      ) : null}

      {data?.attachedFileURL ? (
        <>
          <ExpandedComponentLabel>{"Files"}</ExpandedComponentLabel>
          <ExpandedComponentFileSection>
            {data.attachedFileURL.map((file) => (
              <a
                href={file}
                download={decodeURIComponent(
                  file.split("/")[file.split("/").length - 1]
                )}
              >
                {decodeURIComponent(
                  file.split("/")[file.split("/").length - 1]
                )}
              </a>
            ))}
          </ExpandedComponentFileSection>
        </>
      ) : null}
    </ExpandedTableContainer>
  );
};

const NewsTable = () => {
  const [tableOption, setTableOption] = useState({
    ...defaultTableOption,
    currentRowsPerPage: rowPerPage,
  });
  const [isModalShown, setIsModalShown] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentId, setCommentId] = useState(null);

  const toggleModal = () => setIsModalShown(!isModalShown);

  const navigate = useNavigate();

  const fetchDataTable = () => {
    setTableOption({
      ...tableOption,
      isLoading: true,
    });

    fetchTable({
      url: "/news",
      params: {
        limit: tableOption.currentRowsPerPage,
        offset: tableOption.offset,
        search: tableOption.searchValue,
        column: tableOption.sort.field,
        order: tableOption.sort.direction,
      },
      callback: (data) => {
        setTableOption({
          ...tableOption,
          isLoading: false,
          data: data.rows,
          totalRows: data.count,
        });
      },
    });
  };

  const handleDeleteRow = (row) => {
    Swal.fire({
      title: "Warning",
      // eslint-disable-next-line max-len
      text: `Are you sure you want to delete ${row?.title}`,
      icon: "warning",
      confirmButtonText: "OK",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        genericDelete({
          url: `/news/${row.id}`,
          callback: ({ isSuccess }) => {
            if (isSuccess) {
              fetchDataTable();

              Toast({
                type: 1,
                content: "Successfully deleted news.",
              });
            } else {
              Toast({
                type: 4,
                content: "An error occurred.",
              });
            }
          },
        });
      }
    });
  };

  const fetchComments = (id) => {
    genericGet({
      url: `/news/${id}/comments`,
      callback: (data) => {
        setComments(data);
      },
    });
  };

  const columns = newsColumns({
    fetchComments,
    setCommentId,
    toggleModal,
    navigate,
    handleDeleteRow,
  });

  const onChangePage = (page) => {
    setTableOption({
      ...tableOption,
      page,
      offset: tableOption.currentRowsPerPage * page - 10,
    });
  };

  const onChangeRowsPerPage = (currentRowsPerPage, currentPage) => {
    setTableOption({
      ...tableOption,
      currentRowsPerPage,
      page: currentPage,
    });
  };

  useEffect(() => {
    fetchDataTable();
  }, [
    tableOption.currentRowsPerPage,
    tableOption.offset,
    tableOption.searchValue,
    tableOption.sort.field,
    tableOption.sort.direction,
  ]);

  return (
    <Wrapper>
      <CardSection>
        <CardContainer>
          <HeaderTextSection>
            <HeaderText>{"News & Announcements"}</HeaderText>

            {hasPermission("news-create") && (
              <Link
                to="/news/add"
                state={{ action: ADD_ACTION }}
              >
                <StyledAddButton>
                  <FontAwesomeIcon icon={faBullhorn} />
                  <AddButtonText>{"Add"}</AddButtonText>
                </StyledAddButton>
              </Link>
            )}
          </HeaderTextSection>

          <TableSection>
            <CustomDataTable
              columns={columns}
              data={tableOption.data}
              noHeader
              progressPending={tableOption.isLoading}
              expandableRows
              onChangePage={onChangePage}
              onChangeRowsPerPage={onChangeRowsPerPage}
              expandOnRowClicked
              expandableRowsHideExpander
              expandableRowsComponent={ExpandedComponent}
              height="calc(100vh - 200px)"
              paginationServer
              paginationPerPage={tableOption.currentRowsPerPage}
              paginationTotalRows={tableOption.totalRows}
            />
          </TableSection>
        </CardContainer>
      </CardSection>
      <CommentsModal
        isOpen={isModalShown}
        handleClose={toggleModal}
        comments={comments}
        fetchComments={fetchComments}
        congID={commentId}
        view="news"
        handleFetchTable={fetchDataTable}
      />
    </Wrapper>
  );
};

export default NewsTable;
